/**
 * interviewDraft.js — F-118b word-loss prevention.
 *
 * Real customer (Helen / rtchef45, 2026-07-18) wrote an interview answer twice and lost it BOTH times
 * "before I could save it." One eraser: a control (pre-answer "Ask me something else") wiped an UNSENT
 * typed draft; another gap: the Leave / nav / beforeunload guards only counted SENT turns of >=5 words, so
 * an unsent draft in the box was abandoned with no warning. The law (F-122): no destroy without a warn.
 *
 * An in-progress interview has SUBSTANCE worth protecting if there is a non-empty UNSENT draft in the input
 * (any length — the teller's in-progress words) OR any SENT user turn of >=5 words. Every exit/destroy guard
 * (Leave, in-app nav guard, beforeunload, "Talk to Pip instead" mode-switch) uses this one predicate so the
 * check can never drift between them again.
 */

/** Word count of a trimmed answer (shared threshold logic). */
export function turnWordCount(text) {
  return (text || '').trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Does the live interview hold anything a leave/switch/reload would destroy?
 * @param {{ draft?: string, conversation?: Array<{role:string,content:string}> }} args
 */
export function interviewHasSubstance({ draft, conversation } = {}) {
  if ((draft || '').trim().length > 0) return true; // an unsent draft, ANY length — the F-118b fix
  // F-421 defect 4: ANY sent answer, however short. This was `>= 5` words, and the founder walked
  // straight into the gap — he typed "Hello", tapped Leave, and the interview was gone in one click
  // with no warning. The 5-word floor answers "is there enough here to write up"; this predicate
  // answers "would leaving destroy something". Those are different questions and the floor was
  // borrowed for the wrong one. `turnWordCount` still gates on >= 1, so an empty or whitespace-only
  // turn is still not an answer.
  return (conversation || []).some((t) => t && t.role === 'user' && turnWordCount(t.content) >= 1);
}
